import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ProfileTargeting } from './ProfileTargeting';
import { ProfilePassword } from './ProfilePassword';
import { usePermissions } from '../hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  User, 
  Shield, 
  Settings, 
  Key, 
  Bell, 
  Mail, 
  Calendar,
  Globe,
  Clock,
  ArrowLeft,
  Copy,
  Check,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

interface ProfilePageProps {
  onNavigateBack: () => void;
}

export function ProfilePage({ onNavigateBack }: ProfilePageProps) {
  const { user, getAccessTokenSilently } = useAuth0();
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState('profile');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [userMetadata, setUserMetadata] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // User preferences (these would typically be stored in a database)
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: false,
    weeklyReports: true,
    projectUpdates: true,
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
    : user?.email ? user.email[0].toUpperCase() : 'U';

  useEffect(() => {
    const fetchTokenAndMetadata = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: 'openid profile email read:current_user read:current_user_metadata'
          }
        });
        setAccessToken(token);

        // Extract additional metadata from user object
        const metadata = {
          ...user,
          // Auth0 custom claims might be in different formats
          roles: user['https://demandmanager.com/roles'] || 
                 user['https://lu-commercial-api/roles'] ||
                 user[`${import.meta.env.VITE_AUTH0_AUDIENCE}/roles`] || [],
          permissions: user['https://demandmanager.com/permissions'] ||
                      user['https://lu-commercial-api/permissions'] ||
                      user[`${import.meta.env.VITE_AUTH0_AUDIENCE}/permissions`] || [],
          customClaims: Object.keys(user).filter(key => key.startsWith('https://')),
        };
        setUserMetadata(metadata);
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load complete profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchTokenAndMetadata();
  }, [user, getAccessTokenSilently]);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handlePreferenceChange = (key: string, value: any) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    toast.success('Preference updated');
    // In a real app, you'd save this to your backend
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleString();
  };

  const CopyableField = ({ label, value, fieldName }: { label: string; value: string; fieldName: string }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex items-center space-x-2">
        <Input value={value || 'Not available'} readOnly className="flex-1" />
        <Button
          variant="outline"
          size="icon"
          onClick={() => copyToClipboard(value || '', fieldName)}
          className="shrink-0"
        >
          {copiedField === fieldName ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="icon"
              onClick={onNavigateBack}
              className="shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Profile</h1>
              <p className="text-muted-foreground">Loading your profile data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="icon"
            onClick={onNavigateBack}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Profile</h1>
            <p className="text-muted-foreground">Manage your account settings and preferences</p>
          </div>
        </div>

        {/* Profile Header Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={user?.picture} alt={user?.name || user?.email} />
                <AvatarFallback className="text-lg">{userInitials}</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h3 className="text-2xl font-semibold">{user?.name || 'User'}</h3>
                <p className="text-muted-foreground">{user?.email}</p>
                <div className="flex items-center space-x-2">
                  <Badge variant={permissions.isAdmin ? "default" : "secondary"}>
                    {permissions.isAdmin ? 'Administrator' : 'User'}
                  </Badge>
                  <Badge variant="outline">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="permissions">
              <Key className="w-4 h-4 mr-2" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="preferences">
              <Settings className="w-4 h-4 mr-2" />
              Preferences
            </TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4">
            <ProfileTargeting key={user?.sub} />
            <ProfilePassword key={`password-${user?.sub}`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>Your basic account information from Auth0</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CopyableField label="Full Name" value={user?.name || ''} fieldName="Full Name" />
                  <CopyableField label="Email" value={user?.email || ''} fieldName="Email" />
                  <CopyableField label="User ID" value={user?.sub || ''} fieldName="User ID" />
                  <CopyableField label="Nickname" value={user?.nickname || ''} fieldName="Nickname" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Account Details
                  </CardTitle>
                  <CardDescription>Account status and timestamps</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email Verified</Label>
                    <Badge variant={user?.email_verified ? "default" : "destructive"}>
                      {user?.email_verified ? 'Verified' : 'Not Verified'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <Label>Last Updated</Label>
                    <p className="text-sm text-muted-foreground">{formatDate(user?.updated_at)}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Identity Provider</Label>
                    <p className="text-sm">{user?.sub?.split('|')[0] || 'Auth0'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Custom Claims */}
            {userMetadata?.customClaims && userMetadata.customClaims.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Custom Claims</CardTitle>
                  <CardDescription>Additional data associated with your account</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {userMetadata.customClaims.map((claim: string) => (
                      <div key={claim} className="space-y-2">
                        <Label className="text-sm font-medium">{claim}</Label>
                        <Textarea
                          value={JSON.stringify(user?.[claim], null, 2)}
                          readOnly
                          className="font-mono text-xs"
                          rows={3}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Key className="w-5 h-5 mr-2" />
                  Access Permissions
                </CardTitle>
                <CardDescription>Your current permissions within the Demand Manager system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'canCreateDemands', label: 'Create Demands', description: 'Submit new demand requests' },
                    { key: 'canViewDemandBacklog', label: 'View Demand Backlog', description: 'Access submitted demands' },
                    { key: 'canManageDemands', label: 'Manage Demands', description: 'Edit and update demands' },
                    { key: 'canViewBoardGovernance', label: 'Board Governance', description: 'Access governance features' },
                    { key: 'canViewProjectPortfolio', label: 'Project Portfolio', description: 'View active projects' },
                    { key: 'canViewReports', label: 'Reports', description: 'Access analytics and reports' },
                    { key: 'canManageSettings', label: 'Manage Settings', description: 'Administer Auth0 users, roles, and permissions' },
                    { key: 'isAdmin', label: 'Administrator', description: 'Full system access' },
                  ].map(({ key, label, description }) => (
                    <Card key={key} className={permissions[key as keyof typeof permissions] ? 'border-green-200 bg-green-50' : 'border-gray-200'}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium">{label}</h4>
                              <Badge variant={permissions[key as keyof typeof permissions] ? "default" : "secondary"}>
                                {permissions[key as keyof typeof permissions] ? 'Granted' : 'Denied'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Bell className="w-5 h-5 mr-2" />
                    Notification Settings
                  </CardTitle>
                  <CardDescription>Configure how you receive updates</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive updates via email</p>
                    </div>
                    <Switch
                      checked={preferences.emailNotifications}
                      onCheckedChange={(checked) => handlePreferenceChange('emailNotifications', checked)}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">Browser notifications</p>
                    </div>
                    <Switch
                      checked={preferences.pushNotifications}
                      onCheckedChange={(checked) => handlePreferenceChange('pushNotifications', checked)}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Weekly Reports</Label>
                      <p className="text-sm text-muted-foreground">Summary reports every week</p>
                    </div>
                    <Switch
                      checked={preferences.weeklyReports}
                      onCheckedChange={(checked) => handlePreferenceChange('weeklyReports', checked)}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Project Updates</Label>
                      <p className="text-sm text-muted-foreground">Notifications for project changes</p>
                    </div>
                    <Switch
                      checked={preferences.projectUpdates}
                      onCheckedChange={(checked) => handlePreferenceChange('projectUpdates', checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Globe className="w-5 h-5 mr-2" />
                    Regional Settings
                  </CardTitle>
                  <CardDescription>Language and location preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Input value="English (US)" readOnly />
                  </div>
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Input value={preferences.timezone} readOnly />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Format</Label>
                    <Input value="MM/DD/YYYY" readOnly />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  Security Information
                </CardTitle>
                <CardDescription>Your authentication and security details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Access Token</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      type={showToken ? "text" : "password"}
                      value={accessToken || "No token available"}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowToken(!showToken)}
                      className="shrink-0"
                    >
                      {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(accessToken || '', 'Access Token')}
                      className="shrink-0"
                    >
                      {copiedField === 'Access Token' ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
